import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBrandById } from '../../services/companyAuthService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaEdit, FaBuilding, FaGlobe, FaTags, FaCalendar, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const ViewBrand = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBrandDetails();
  }, [id]);

  const fetchBrandDetails = async () => {
    try {
      setLoading(true);
      const response = await getBrandById(id);
      if (response.success) {
        setBrand(response.brand);
      } else {
        toast.error('Failed to fetch brand details');
        navigate('/company/dashboard/brands');
      }
    } catch (error) {
      console.error('Error fetching brand:', error);
      toast.error(error.message || 'Error fetching brand details');
      navigate('/company/dashboard/brands');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className="flex justify-center items-center min-h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
        </div>
      </>
    );
  }

  if (!brand) {
    return (
      <>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <p className="text-gray-600">Brand not found</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="min-h-full bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          {/* Back Button - Outside the card */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/company/dashboard/brands/')}
              className="inline-flex items-center px-2 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 hover:border-orange-200 transition-colors duration-200"
            >
              ← Back
            </button>
          </div>
          
          {/* Header Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h1 className="md:text-3xl text-2xl font-bold text-gray-900">Brand Details</h1>
              </div>
              
              {brand.isActive && (
                <button
                  onClick={() => navigate(`/company/dashboard/brands/edit/${id}`)}
                  className="flex items-center md:px-6 px-2 md:py-3 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors duration-200 shadow-sm"
                >
                  <FaEdit className="mr-2" />
                  Edit Brand
                </button>
              )}
            </div>
          </div>

          {/* Main Content Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-10 lg:mb-0">
            {/* Brand Header */}
            <div className="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <FaBuilding className="text-2xl text-orange-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{brand.brandName}</h2>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      brand.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {brand.isActive ? (
                        <>
                          <FaCheckCircle className="mr-1" />
                          Active
                        </>
                      ) : (
                        <>
                          <FaTimesCircle className="mr-1" />
                          Inactive
                        </>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Brand Information Grid */}
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Basic Information */}
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                    Basic Information
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <FaGlobe className="text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Country</p>
                        <p className="text-lg text-gray-900">{brand.country}</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <FaTags className="text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Category</p>
                        <p className="text-lg text-gray-900">{brand.category}</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <FaTags className="text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Subcategory</p>
                        <p className="text-lg text-gray-900">{brand.subcategory}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Details */}
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                    Additional Details
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <FaCalendar className="text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Created At</p>
                        <p className="text-lg text-gray-900">
                          {new Date(brand.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>

                    {/* <div className="flex items-start space-x-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <FaBuilding className="text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Brand ID</p>
                        <p className="text-sm text-gray-600 font-mono">{brand._id}</p>
                      </div>
                    </div> */}
                  </div>
                </div>
              </div>

              {/* Status Information */}
              {!brand.isActive && (
                <div className="mt-8 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FaTimesCircle className="text-red-500 text-xl" />
                    <div>
                      <h4 className="text-sm font-medium text-red-800">Brand Inactive</h4>
                      <p className="text-sm text-red-600 mt-1">
                        This brand is currently inactive and cannot be edited. Contact support if you need to reactivate it.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default ViewBrand; 